/**
 * Web FormLabel Component
 *
 */

import React, { useEffect, useRef } from 'react'
import clsx from 'clsx'
import {
  extendPropsWithContext,
  isTrue,
  validateDOMAttributes,
} from '../../shared/component-helper'
import { createSpacingClasses } from '../space/SpacingHelper'
import {
  createSkeletonClass,
  skeletonDOMAttributes,
} from '../skeleton/SkeletonHelper'
import {
  FormElementProps,
  pickFormElementProps,
} from '../../shared/helpers/filterValidProps'
import { omitSpacingProps } from '../flex/utils'
import Context from '../../shared/Context'
import type {
  DynamicElement,
  DynamicElementParams,
  SpacingProps,
} from '../../shared/types'

export type FormLabelProps = {
  forId?: string
  element?: DynamicElement<HTMLLabelElement>
  text?: React.ReactNode
  size?: 'basis' | 'medium' | 'large'
  id?: string
  skeleton?: boolean
  label?: React.ReactNode
  vertical?: boolean
  srOnly?: boolean
  innerRef?: React.RefObject<HTMLElement>

  /** Is not a part of HTMLLabelElement and not documented as of now */
  disabled?: boolean

  /**
   * For internal use only
   */
  labelDirection?: FormElementProps['labelDirection']
}

export type FormLabelAllProps = FormLabelProps &
  React.HTMLAttributes<HTMLLabelElement> &
  SpacingProps

export default function FormLabel(localProps: FormLabelAllProps) {
  const context = React.useContext(Context)

  // use only the props from context, who are available here anyway
  const props = extendPropsWithContext(
    localProps,
    null,
    { skeleton: context?.skeleton },
    pickFormElementProps(context?.formElement),
    context?.FormLabel
  )

  const nestedContent = props?.text || props?.children
  const nestedNode =
    nestedContent?.['type'] === FormLabel ? nestedContent?.['type'] : null
  const nestedElement = nestedNode
    ? () => React.createElement(nestedNode, nestedContent['props'])
    : null

  const {
    forId,
    text,
    srOnly,
    vertical,
    labelDirection,
    size,
    skeleton,
    element: Element = nestedElement || 'label',
    innerRef,
    className,
    children,
    ...attributes
  } = props

  const content = text || children

  const isInteractive = Boolean(
    !props.disabled &&
      !srOnly &&
      (typeof props.onClick === 'function' || forId)
  )

  const params = {
    className: clsx(
      'dnb-form-label',
      (isTrue(vertical) || labelDirection === 'vertical') &&
        `dnb-form-label--vertical`,
      srOnly && 'dnb-sr-only',
      size && `dnb-h--${size}`,
      isInteractive && 'dnb-form-label--interactive',
      createSkeletonClass('font', skeleton, context),
      createSpacingClasses(
        content ? { right: 'small', ...props } : omitSpacingProps(props)
      ),
      className
    ),
    htmlFor: forId,
    ...(attributes as DynamicElementParams),
  }

  const labelRef = useRef<HTMLLabelElement>(null)
  const ref = innerRef || labelRef
  if (!nestedNode) {
    params['ref'] = ref
  }

  useEffect(() => {
    if (!forId) {
      return
    }

    const forElem = document.querySelector(`#${forId}`)
    const target =
      forElem?.closest('.dnb-input__border--root') ||
      forElem?.closest('.dnb-input__border')

    if (target && ref.current) {
      const elem = ref.current

      const buttonEnter = () => {
        target.classList.add('no-hover')
        target.classList.remove('hover')
      }
      const buttonLeave = () => {
        target.classList.remove('no-hover')
        enter()
      }

      const enter = () => {
        target.classList.add('hover')

        // Remove the style from interactive elements (e.g. HelpButton, TermDefinition)
        const button = elem.querySelector('button')
        button?.addEventListener?.('mouseenter', buttonEnter, {
          once: true,
        })
        button?.addEventListener?.('mouseleave', buttonLeave, {
          once: true,
        })

        // Also handle elements with role="button" (e.g. TermDefinition)
        const interactiveElements = elem.querySelectorAll(
          '[role="button"]:not(button)'
        )
        interactiveElements.forEach((interactiveElem) => {
          interactiveElem.addEventListener('mouseenter', buttonEnter, {
            once: true,
          })
          interactiveElem.addEventListener('mouseleave', buttonLeave, {
            once: true,
          })
        })
      }
      const leave = () => {
        target.classList.remove('hover')

        const button = elem.querySelector('button')
        button?.removeEventListener?.('mouseenter', buttonEnter)

        // Also remove listeners from elements with role="button"
        const interactiveElements = elem.querySelectorAll(
          '[role="button"]:not(button)'
        )
        interactiveElements.forEach((interactiveElem) => {
          interactiveElem.removeEventListener('mouseenter', buttonEnter)
          interactiveElem.removeEventListener('mouseleave', buttonLeave)
        })
      }

      elem.addEventListener('mouseenter', enter)
      elem.addEventListener('mouseleave', leave)

      return () => {
        if (elem) {
          elem.removeEventListener('mouseenter', enter)
          elem.removeEventListener('mouseleave', leave)

          const button = elem.querySelector('button')
          button?.removeEventListener?.('mouseleave', buttonLeave)

          // Also clean up listeners from elements with role="button"
          const interactiveElements = elem.querySelectorAll(
            '[role="button"]:not(button)'
          )
          interactiveElements.forEach((interactiveElem) => {
            interactiveElem.removeEventListener('mouseenter', buttonEnter)
            interactiveElem.removeEventListener('mouseleave', buttonLeave)
          })
        }
      }
    }
  }, [forId, ref])

  skeletonDOMAttributes(params, skeleton, context)
  validateDOMAttributes(localProps, params)

  return <Element {...params}>{content}</Element>
}

FormLabel._formElement = true
FormLabel._supportsSpacingProps = true
